import React from 'react';
import { Overlay } from 'react-overlays';
import classNames from 'classnames';

import styles from './tooltip.scss';
import scrollParent from '../../services/scroll-parent';
import { SIDE_POSITION as POSITION } from '../../constants/positioning';

const DEFAULT_TIMEOUT = 500;

interface TooltipProps {
  text: string;
  placement?: POSITION;
  timeout?: number;
  targetRef?: HTMLElement | null;
  className?: string;
  style?: React.CSSProperties;
  isInfoTooltip?: boolean;
}

interface TooltipState {
  isVisible: boolean;
}

interface ElementProps {
  ref?: string | ((instance: HTMLDivElement | null) => void) | React.RefObject<HTMLDivElement>;
  style?: React.CSSProperties;
  children?: Element;
}

interface PassDownProps {
  props: ElementProps;
  arrowProps?: ElementProps;
  placement: string;
}

export class Tooltip extends React.Component<TooltipProps, TooltipState> {
  public static defaultProps: Partial<TooltipProps> = {
    placement: POSITION.TOP,
    timeout: DEFAULT_TIMEOUT,
    targetRef: null,
  };

  contentRef: null | HTMLElement = null;
  timeout = 0;

  state = {
    isVisible: false,
  };

  componentWillUnmount() {
    this.handleMouseLeave();
  }

  handleMouseEnter = () => {
    this.timeout = window.setTimeout(() => {
      this.setState({ isVisible: true });
    }, this.props.timeout);
  };

  handleMouseLeave = () => {
    clearTimeout(this.timeout);
    this.setState({ isVisible: false });
  };

  setContentRef = (ref: HTMLElement | null) => {
    this.contentRef = ref;
  };

  renderTooltip(options: PassDownProps) {
    const { props, placement, arrowProps } = options;
    const { text, style, isInfoTooltip } = this.props;

    return (
      <div
        ref={props.ref}
        className={classNames(styles.contentContainer, styles[placement], styles.fullOpacity)}
        style={{
          ...style,
          ...props.style,
        }}
      >
        <div className={classNames(styles.content, 'ma-font-body-m', { [styles.info]: isInfoTooltip })}>{text}</div>
        <div {...arrowProps} className={styles.triangleWrapper}>
          <div className={classNames(styles.triangle, { [styles.info]: isInfoTooltip })} />
        </div>
      </div>
    );
  }

  render() {
    const { children, placement, className } = this.props;

    return (
      <div
        ref={this.setContentRef}
        className={className}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        {children}
        <Overlay show={this.state.isVisible} placement={placement} container={scrollParent()} target={this.contentRef}>
          {(options: PassDownProps) => this.renderTooltip(options)}
        </Overlay>
      </div>
    );
  }
}

export default Tooltip;
